import * as React from "react";
import { useLayoutEffect, useRef } from "react";
import styled from "styled-components";
import * as h from "../../_themes/helpers";

interface IProps {
  value: number;
  isDragging: boolean;
  onUpdate: (value: number) => void;
  setIsDragging?(value: boolean): void;
}

export function Thumb({
  onUpdate,
  value: currentValue,
  isDragging,
  setIsDragging
}: IProps) {
  const el = useRef<HTMLDivElement>(null);
  const y = useRef(currentValue);

  const onMouseDown = () => setIsDragging(true);
  const onMouseUp = () => {
    if (isDragging) {
      onUpdate(y.current);
    }
    setIsDragging(false);
  };

  useLayoutEffect(() => {
    const { current: thumb } = el;
    const {
      top,
      height
    } = thumb.parentElement.getBoundingClientRect() as DOMRect;
    const TRACK_HEIGHT = 4.7;
    // init the Y position with the current value
    thumb.style.transform = `translate(0, ${TRACK_HEIGHT -
      currentValue * 0.01 * TRACK_HEIGHT -
      0.4}em)`;

    const onMouseMove = (event: MouseEvent) => {
      event.preventDefault();

      if (!isDragging) {
        return;
      }

      // value based on the coord y
      const min = Math.min(1, (event.clientY - top) / height);
      const max = Math.max(0, min);
      const value = 1 - max;

      // use transform css instead of calling style-components update
      thumb.style.transform = `translate(0, ${TRACK_HEIGHT -
        value * TRACK_HEIGHT -
        0.4}em)`;

      const nextValue = Math.floor(value * 100);
      y.current = nextValue;

      if (nextValue !== currentValue) {
        onUpdate(nextValue);
      }
    };

    // we can't assume that the user will up in the component
    // so we need to listen in the document
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("mousemove", onMouseMove);

    return () => {
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("mousemove", onMouseMove);
    };
  });

  return <ThumbView ref={el} onMouseDown={onMouseDown} />;
}

const ThumbView = styled.div`
  border-radius: 100%;
  height: 0.8em;
  width: 0.8em;
  position: absolute;
  left: calc(50% - 0.4em);
  z-index: 3;
  background-color: ${h.getBackgroundColor(
    "sliderHandleStyle",
    "slider.handleStyle"
  )};
  outline: 0;
  box-shadow: 0 1px 0.3em 1px rgba(0, 0, 0, 0.5);
  will-change: transform;
  transform: translate(0, 5em);
  cursor: grab;
  &:active {
    cursor: grabbing;
  }
`;
